import { r as createLogger } from "./logger-BU8YqMGU.js";
import { t as geodesicAngleRad } from "./geodesic-angle-DbbTJqSa.js";
import { DEFAULT_MOTION_FILTER, MotionWindow, decideCapture } from "./ar/capture-motion-gate.js";
import { DEFAULT_QUALITY_FILTER } from "./ar/image-quality.js";
import { quat } from "gl-matrix";
//#region ../src/ar/pose-motion.ts
/**
* Pose-motion helpers — stateless angular/linear velocity from two AR poses.
*
* The capture motion gate (blurry-frame skipping, see
* `GpsPlusSlamJs_Docs/docs/2026-06-23-2105-blurry-frame-motion-gating-plan.md` §4.1)
* rejects frames whose device motion is too fast to yield a sharp image. Motion
* is derived from consecutive WebXR poses — cheaper than any image-content
* metric because it needs no GPU readback.
*
* This module holds ONLY the pure, stateless math (angle ÷ dt, distance ÷ dt).
* The stateful policy (sliding window, glitch rejection, the capture decision)
* lives in `ImageCaptureManager` and `capture-motion-gate.ts` — keeping the
* reusable atom separate from the capture-specific policy (plan §4.1 "Scope").
*
* Angular velocity reuses the shared `geodesicAngleRad` kernel rather than a
* fresh `acos` (CLAUDE.md: search first, prefer refactor over re-implementation).
*/
const _scratchQuatPrev = quat.create();
const _scratchQuatCur = quat.create();
/**
* Write a WebXR object-form quaternion into `out`, normalized. Normalizing
* defends against slightly non-unit inputs (the gate must not throw on
* imperfect tracking data).
*/
function toGlQuat(q, out) {
	quat.set(out, q.x, q.y, q.z, q.w);
	return quat.normalize(out, out);
}
/**
* Angular velocity in radians/second between two orientations.
*
* @param qPrev - orientation at the previous frame.
* @param qCur - orientation at the current frame.
* @param dtSeconds - elapsed time in seconds (must be > 0).
* @returns geodesic angle between the orientations divided by `dtSeconds`.
*   Returns `0` when `dtSeconds <= 0` (degenerate/duplicate frame timestamp) so
*   a bad delta can never produce `Infinity`/`NaN` and spuriously flip the
*   motion gate. Double-cover safe (`q` ≡ `−q`) via the shared kernel.
*/
function angularVelocity(qPrev, qCur, dtSeconds) {
	if (!(dtSeconds > 0)) return 0;
	return geodesicAngleRad(toGlQuat(qPrev, _scratchQuatPrev), toGlQuat(qCur, _scratchQuatCur)) / dtSeconds;
}
/**
* Linear velocity in metres/second between two positions.
*
* @param pPrev - position at the previous frame.
* @param pCur - position at the current frame.
* @param dtSeconds - elapsed time in seconds (must be > 0).
* @returns straight-line distance divided by `dtSeconds`. Returns `0` when
*   `dtSeconds <= 0` (same guard rationale as {@link angularVelocity}).
*/
function linearVelocity(pPrev, pCur, dtSeconds) {
	if (!(dtSeconds > 0)) return 0;
	const dx = pCur.x - pPrev.x;
	const dy = pCur.y - pPrev.y;
	const dz = pCur.z - pPrev.z;
	return Math.sqrt(dx * dx + dy * dy + dz * dz) / dtSeconds;
}
//#endregion
//#region ../src/ar/image-capture.ts
/**
* Image Capture Module
*
* Captures periodic JPEG screenshots from the WebGL canvas during AR recording.
* Uses async toBlob() for better performance (non-blocking).
*
* Includes validation to detect suspiciously small images (likely black/empty)
* which can occur on mobile devices when the WebGL context hasn't composited yet.
*/
const log = createLogger("ImageCapture");
/**
* Minimum expected blob size in bytes for a valid JPEG image.
* A black/empty 1920x1080 JPEG still compresses to ~2-3KB due to headers.
* A real camera frame at 0.7 quality should be 50KB+.
* We use a conservative threshold to catch obviously broken captures.
*/
const MIN_VALID_IMAGE_BYTES = 5e3;
/**
* Default capture configuration
*/
const DEFAULT_CAPTURE_CONFIG = {
	intervalMs: 2e3,
	quality: .7,
	captureTimeoutMs: 5e3,
	resolutionDivisor: 1,
	motionFilter: DEFAULT_MOTION_FILTER,
	qualityFilter: DEFAULT_QUALITY_FILTER
};
/**
* Manages periodic image capture from a canvas.
*
* Usage:
* 1. Create with canvas and callbacks
* 2. Call start() when recording begins
* 3. Call onFrame(time) each XR frame
* 4. Call stop() when recording ends
*/
var ImageCaptureManager = class {
	canvas;
	callbacks;
	config;
	capturing = false;
	lastCaptureTime = 0;
	frameCount = 0;
	captureInProgress = false;
	captureTimeoutId = null;
	/** True while an `analyzeFrame` verdict is in flight; blocks a second capture
	*  for the same interval without throttling normal cadence (captureInProgress
	*  is released after encode, NOT held across the round-trip). */
	awaitingVerdict = false;
	/** The current interval's capture was rejected by the quality gate; bypass the
	*  interval guard so the next acceptable frame is grabbed immediately. */
	retryPending = false;
	/** Frame time (ms) of the FIRST quality attempt for the current interval; the
	*  quality `maxWaitMs` fallback measures from here, independent of the motion
	*  gate's own due clock. `null` between intervals. */
	qualityDeadlineBase = null;
	/** Safety timeout for a hung `analyzeFrame` (fail-open). */
	verdictTimeoutId = null;
	/** Sliding window of recent per-frame velocities for the motion gate. */
	motionWindow = new MotionWindow();
	/** Previous frame's pose, for the per-frame velocity delta. */
	prevPose = null;
	/** Previous frame's timestamp (ms), for the per-frame velocity delta. */
	prevTime = 0;
	/** Whether THIS frame's velocity sample was rejected as a tracking glitch. */
	lastSampleWasGlitch = false;
	/** When the current capture first became due (ms); null while not due. The
	*  `maxWaitMs` fallback measures from here, not from `lastCaptureTime`. */
	dueTime = null;
	constructor(canvas, callbacks, config = DEFAULT_CAPTURE_CONFIG) {
		this.canvas = canvas;
		this.callbacks = callbacks;
		this.config = config;
	}
	/**
	* Start capturing images.
	* Resets frame counter and last capture time.
	*/
	start() {
		this.capturing = true;
		this.lastCaptureTime = 0;
		this.frameCount = 0;
		this.motionWindow.reset();
		this.prevPose = null;
		this.prevTime = 0;
		this.lastSampleWasGlitch = false;
		this.dueTime = null;
		this.clearVerdictTimeout();
		this.awaitingVerdict = false;
		this.retryPending = false;
		this.qualityDeadlineBase = null;
	}
	/**
	* Stop capturing images.
	* Clears any pending safety timeout and resets in-flight capture state.
	*/
	stop() {
		this.capturing = false;
		this.clearCaptureTimeout();
		this.captureInProgress = false;
		this.clearVerdictTimeout();
		this.awaitingVerdict = false;
		this.retryPending = false;
		this.qualityDeadlineBase = null;
	}
	/**
	* Check if capture manager is active.
	*/
	isCapturing() {
		return this.capturing;
	}
	/**
	* Get the current frame count.
	*/
	getFrameCount() {
		return this.frameCount;
	}
	/**
	* Called each XR frame to check if a capture is needed.
	*
	* @param time - Frame timestamp in milliseconds (from requestAnimationFrame)
	*/
	onFrame(time) {
		if (!this.capturing) return;
		const pose = this.callbacks.getCurrentPose();
		this.updateMotionWindow(time, pose);
		if (this.captureInProgress || this.awaitingVerdict) return;
		const elapsed = time - this.lastCaptureTime;
		if (!this.retryPending && this.lastCaptureTime > 0 && elapsed < this.config.intervalMs) {
			this.dueTime = null;
			return;
		}
		if (!pose) return;
		if (this.dueTime === null) this.dueTime = time;
		if (!this.shouldCaptureNow(time)) return;
		this.captureInProgress = true;
		this.lastCaptureTime = time;
		this.dueTime = null;
		const timestamp = performance.timeOrigin + time;
		const screenRotation = this.callbacks.getScreenRotation();
		this.captureTimeoutId = setTimeout(() => {
			if (this.captureInProgress) {
				log.error(`Capture timeout after ${this.config.captureTimeoutMs}ms — force-resetting captureInProgress`);
				this.captureInProgress = false;
				this.captureTimeoutId = null;
			}
		}, this.config.captureTimeoutMs);
		if (this.callbacks.captureFrame) this.callbacks.captureFrame(this.config.quality).then((frame) => {
			this.handleCapturedBlob(frame?.blob ?? null, frame?.width, frame?.height, timestamp, time, pose, screenRotation);
		}).catch(() => {
			this.clearCaptureTimeout();
			this.captureInProgress = false;
			this.callbacks.onCaptureFailed?.();
		});
		else this.canvas.toBlob((blob) => {
			this.handleCapturedBlob(blob, this.canvas.width, this.canvas.height, timestamp, time, pose, screenRotation);
		}, "image/jpeg", this.config.quality);
	}
	/**
	* Sample per-frame device motion into the sliding window. Called every frame
	* (even during an in-flight capture or before the interval elapses) so the
	* window reflects truly instantaneous motion the moment a capture becomes
	* due. A frame with no pose, or a non-positive dt (duplicate timestamp),
	* records no sample and is treated as "not a glitch".
	*/
	updateMotionWindow(time, pose) {
		if (!pose) {
			this.lastSampleWasGlitch = false;
			this.motionWindow.reset();
			this.prevPose = null;
			this.prevTime = 0;
			this.retryPending = false;
			this.qualityDeadlineBase = null;
			return;
		}
		if (this.prevPose && this.prevTime > 0) {
			const dt = (time - this.prevTime) / 1e3;
			if (dt > 0) {
				const angVel = angularVelocity(this.prevPose.orientation, pose.orientation, dt);
				const linVel = linearVelocity(this.prevPose.position, pose.position, dt);
				this.lastSampleWasGlitch = !this.motionWindow.push(angVel, linVel);
			} else this.lastSampleWasGlitch = false;
		} else this.lastSampleWasGlitch = false;
		this.prevPose = pose;
		this.prevTime = time;
	}
	/**
	* Apply the motion gate to a due capture. Returns `true` to capture now,
	* `false` to defer to a later frame.
	*
	* - Filter disabled → always `true` (legacy behavior).
	* - `maxWaitMs` elapsed → always `true` (never-calm safety fallback; an
	*   interval is never silently lost).
	* - Current frame's sample was a tracking glitch → `false` (don't grab a
	*   relocalization-teleport frame; the fallback still guarantees progress).
	* - No measurable motion yet (first capture / all-glitch) → `true` (the very
	*   first capture is never blocked; only measurable motion defers).
	* - Otherwise → defer to {@link decideCapture} over the windowed maxima.
	*/
	shouldCaptureNow(time) {
		const mf = this.config.motionFilter;
		if (!mf || !mf.enabled) return true;
		const msSinceDue = this.dueTime === null ? 0 : time - this.dueTime;
		if (msSinceDue >= mf.maxWaitMs) return true;
		if (this.lastSampleWasGlitch) return false;
		if (!this.motionWindow.hasSamples()) return true;
		return decideCapture({
			windowMaxAngular: this.motionWindow.maxAngular(),
			windowMaxLinear: this.motionWindow.maxLinear(),
			maxAngularVelocity: mf.maxAngularVelocity,
			maxLinearVelocity: mf.maxLinearVelocity,
			msSinceDue,
			maxWaitMs: mf.maxWaitMs
		}) === "capture";
	}
	/**
	* Clear the safety timeout for captureInProgress.
	* Called when capture completes (success or failure) before the timeout fires.
	*/
	clearCaptureTimeout() {
		if (this.captureTimeoutId !== null) {
			clearTimeout(this.captureTimeoutId);
			this.captureTimeoutId = null;
		}
	}
	/** Clear the safety timeout for a hung `analyzeFrame` verdict. */
	clearVerdictTimeout() {
		if (this.verdictTimeoutId !== null) {
			clearTimeout(this.verdictTimeoutId);
			this.verdictTimeoutId = null;
		}
	}
	/**
	* Common handler for captured blobs (from either canvas.toBlob or captureFrame).
	* Releases `captureInProgress` after the synchronous encode, then either saves
	* immediately (legacy / gate-off) or runs the off-thread image-quality gate.
	*
	* @param time - the XR frame time the capture was dispatched at; the quality
	*   retry-deadline clock and `lastCaptureTime` are measured from it.
	*/
	handleCapturedBlob(blob, width, height, timestamp, time, pose, screenRotation) {
		this.clearCaptureTimeout();
		this.captureInProgress = false;
		if (!blob) {
			this.callbacks.onCaptureFailed?.();
			return;
		}
		const qf = this.config.qualityFilter;
		const analyze = this.callbacks.analyzeFrame;
		if (!analyze || !qf || !qf.enabled) {
			this.saveCapture(blob, width, height, timestamp, pose, screenRotation);
			return;
		}
		if (this.qualityDeadlineBase === null) this.qualityDeadlineBase = time;
		if (time - this.qualityDeadlineBase >= qf.maxWaitMs) {
			this.saveCapture(blob, width, height, timestamp, pose, screenRotation);
			return;
		}
		this.awaitingVerdict = true;
		let settled = false;
		const finish = (accept, viaTimeout) => {
			if (settled || !this.capturing) return;
			settled = true;
			this.clearVerdictTimeout();
			this.awaitingVerdict = false;
			if (viaTimeout) log.error("analyzeFrame verdict timed out — saving frame fail-open to keep the pipeline live");
			if (accept) this.saveCapture(blob, width, height, timestamp, pose, screenRotation);
			else this.retryPending = true;
		};
		this.verdictTimeoutId = setTimeout(() => finish(true, true), this.config.captureTimeoutMs);
		analyze({
			blob,
			width: width ?? 0,
			height: height ?? 0
		}).then((verdict) => finish(verdict.accept, false)).catch((err) => {
			log.error("analyzeFrame failed — saving frame without quality gate", err);
			finish(true, false);
		});
	}
	/**
	* Persist an accepted frame: allocate its 1-based index, run the suspicious-
	* size belt-and-suspenders check, reset the per-interval gate clocks, and fire
	* `onCaptured`. Called from the legacy path, the quality fallbacks, and an
	* accepting verdict — the single place a frame becomes durable.
	*/
	saveCapture(blob, width, height, timestamp, pose, screenRotation) {
		if (!this.capturing) return;
		this.retryPending = false;
		this.qualityDeadlineBase = null;
		const frameIndex = ++this.frameCount;
		if (blob.size < 5e3) {
			log.error(`Suspicious image at frame ${frameIndex}: blob size ${blob.size} bytes is below minimum ${MIN_VALID_IMAGE_BYTES} bytes. Image may be black/empty.`);
			this.callbacks.onSuspiciousImage?.(blob.size, frameIndex);
		}
		this.callbacks.onCaptured({
			blob,
			timestamp,
			frameIndex,
			position: pose.position,
			rotation: pose.orientation,
			screenRotation,
			...width && width > 0 ? { width } : {},
			...height && height > 0 ? { height } : {}
		});
	}
};
//#endregion
export { ImageCaptureManager as n, MIN_VALID_IMAGE_BYTES as r, DEFAULT_CAPTURE_CONFIG as t };
